import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { getTestTableData, convertToPricingData } from '../utils/testTableData';

interface PricingInputGridProps {
  onComplete: (pricingData: PricingData) => void;
  onBack: () => void;
  initialData?: PricingData;
  isTestMode?: boolean;
}

interface PricingData {
  tiers: number;
  billingPeriods: string[];
  pricingGrid: {
    [tier: number]: {
      [period: string]: {
        currentPrice: string;
        currentLink: string;
        defaultPrice: string;
        defaultLink: string;
        testPrices: Array<{
          price: string;
          link: string;
        }>;
      };
    };
  };
}

// Modal data interface for the new format
interface PricingModalData {
  currentPrice: string;
  currentLink: string;
  defaultPrice: string;
  defaultLink: string;
  testPrices: Array<{
    price: string;
    link: string;
  }>;
}

interface PricingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (tier: number, period: string, data: PricingModalData) => void;
  onContinue: (tier: number, period: string, data: PricingModalData) => void;
  tierNumber: number;
  billingPeriod: string;
  initialData?: PricingModalData;
}

// Helper function to strip currency symbols, letters, and spaces
const sanitizePriceInput = (value: string): string => {
  // Remove currency symbols, letters, spaces, and keep only numbers and decimal point
  return value.replace(/[^\d.]/g, '');
};

const PricingModal: React.FC<PricingModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onContinue,
  tierNumber,
  billingPeriod,
  initialData
}) => {
  const [currentPrice, setCurrentPrice] = useState(initialData?.currentPrice || '');
  const [currentLink, setCurrentLink] = useState(initialData?.currentLink || '');
  const [defaultPrice, setDefaultPrice] = useState(initialData?.defaultPrice || '');
  const [defaultLink, setDefaultLink] = useState(initialData?.defaultLink || '');
  const [testPrices, setTestPrices] = useState<Array<{ price: string; link: string }>>(
    initialData?.testPrices || []
  );

  // Reset form when tier or period changes
  useEffect(() => {
    setCurrentPrice(initialData?.currentPrice || '');
    setCurrentLink(initialData?.currentLink || '');
    setDefaultPrice(initialData?.defaultPrice || '');
    setDefaultLink(initialData?.defaultLink || '');
    setTestPrices(initialData?.testPrices || []);
  }, [tierNumber, billingPeriod, initialData]);

  const addTestPrice = () => {
    if (testPrices.length < 3) { // Max 3 test prices since default is separate
      setTestPrices([...testPrices, { price: '', link: '' }]);
    }
  };

  const removeTestPrice = (index: number) => {
    setTestPrices(testPrices.filter((_, i) => i !== index));
  };

  const updateTestPrice = (index: number, field: 'price' | 'link', value: string) => {
    const updated = [...testPrices];
    // Sanitize price input to remove currency symbols
    const sanitizedValue = field === 'price' ? sanitizePriceInput(value) : value;
    updated[index] = { ...updated[index], [field]: sanitizedValue };
    setTestPrices(updated);
  };

  const handleSave = () => {
    const data: PricingModalData = {
      currentPrice,
      currentLink,
      defaultPrice: defaultPrice || currentPrice, // Use currentPrice as default if defaultPrice is empty
      defaultLink: defaultLink || currentLink, // Use currentLink as default if defaultLink is empty
      testPrices
    };
    onSave(tierNumber, billingPeriod, data);
    onClose();
  };

  const handleContinue = () => {
    const data: PricingModalData = {
      currentPrice,
      currentLink,
      defaultPrice: defaultPrice || currentPrice, // Use currentPrice as default if defaultPrice is empty
      defaultLink: defaultLink || currentLink, // Use currentLink as default if defaultLink is empty
      testPrices
    };
    onContinue(tierNumber, billingPeriod, data);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Configure Pricing - Tier {tierNumber} ({billingPeriod})
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Current Price Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Current Price</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="currentPrice">Price</Label>
                <Input
                  id="currentPrice"
                  type="text"
                  placeholder="e.g., 9"
                  value={currentPrice}
                  onChange={(e) => setCurrentPrice(sanitizePriceInput(e.target.value))}
                />
              </div>
              <div>
                <Label htmlFor="currentLink">Link</Label>
                <Input
                  id="currentLink"
                  type="url"
                  placeholder="https://example.com/signup"
                  value={currentLink}
                  onChange={(e) => setCurrentLink(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Default Price Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Default Price (Fallback)</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="defaultPrice">Price</Label>
                <Input
                  id="defaultPrice"
                  type="text"
                  placeholder="e.g., 8"
                  value={defaultPrice}
                  onChange={(e) => setDefaultPrice(sanitizePriceInput(e.target.value))}
                />
              </div>
              <div>
                <Label htmlFor="defaultLink">Link</Label>
                <Input
                  id="defaultLink"
                  type="url"
                  placeholder="https://example.com/signup"
                  value={defaultLink}
                  onChange={(e) => setDefaultLink(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Test Prices Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Test Prices</h3>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addTestPrice}
                disabled={testPrices.length >= 3}
              >
                Add Test Price
              </Button>
            </div>
            
            {testPrices.map((testPrice, index) => (
              <div key={index} className="grid grid-cols-3 gap-4 items-end">
                <div>
                  <Label htmlFor={`testPrice-${index}`}>Price</Label>
                  <Input
                    id={`testPrice-${index}`}
                    type="text"
                    placeholder="e.g., 10"
                    value={testPrice.price}
                    onChange={(e) => updateTestPrice(index, 'price', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor={`testLink-${index}`}>Link</Label>
                  <Input
                    id={`testLink-${index}`}
                    type="url"
                    placeholder="https://example.com/signup"
                    value={testPrice.link}
                    onChange={(e) => updateTestPrice(index, 'link', e.target.value)}
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => removeTestPrice(index)}
                >
                  Remove
                </Button>
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave}>
              Save
            </Button>
            <Button onClick={handleContinue}>
              Save & Continue
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const PricingInputGrid: React.FC<PricingInputGridProps> = ({
  onComplete,
  onBack,
  initialData,
  isTestMode = false
}) => {
  const [tiers, setTiers] = useState(initialData?.tiers || 3);
  const [billingPeriods, setBillingPeriods] = useState<string[]>(
    initialData?.billingPeriods || ['monthly', 'annually']
  );
  const [pricingGrid, setPricingGrid] = useState<PricingData['pricingGrid']>(
    initialData?.pricingGrid || {}
  );
  const [selectedTier, setSelectedTier] = useState<number | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<string | null>(null);

  // Handler for predefined data selection
  const handlePredefinedDataSelect = (value: string) => {
    console.log('🔄 Selecting predefined data:', value);
    const testTableConfig = getTestTableData(value);
    if (testTableConfig) {
      console.log('✅ Loading predefined data for', value);
      const predefinedData = convertToPricingData(testTableConfig);
      
      // Update state with predefined data
      setTiers(predefinedData.tiers);
      setBillingPeriods(predefinedData.billingPeriods);
      setPricingGrid(predefinedData.pricingGrid);
      
      console.log('💾 Predefined data loaded:', predefinedData);
    } else {
      console.error('❌ No predefined data found for', value);
    }
  };

  // Handler to clear all data
  const clearAllData = () => {
    console.log('🗑️ Clearing all pricing data');
    setTiers(3);
    setBillingPeriods(['monthly', 'annually']);
    setPricingGrid({});
    setSelectedTier(null);
    setSelectedPeriod(null);
  };

  // Initialize pricing grid
  useEffect(() => {
    const newGrid: PricingData['pricingGrid'] = {};
    for (let tier = 1; tier <= tiers; tier++) {
      newGrid[tier] = {};
      for (const period of billingPeriods) {
        if (isTestMode) {
          // Auto-populate test data in test mode
          const basePrices = {
            1: { monthly: 9, annually: 7 },
            2: { monthly: 29, annually: 23 },
            3: { monthly: 99, annually: 79 },
            4: { monthly: 199, annually: 139 },
            5: { monthly: 399, annually: 279 }
          };
          
          const basePrice = basePrices[tier as keyof typeof basePrices]?.[period as keyof typeof basePrices[1]] || 10;
          
          // Map tier to actual plan names used in HTML
          const tierLinks = {
            1: 'https://example.com/signup/starter',
            2: 'https://example.com/signup/professional', 
            3: 'https://example.com/contact-sales',
            4: 'https://example.com/signup/enterprise-plus',
            5: 'https://example.com/signup/enterprise-pro'
          };
          
          newGrid[tier][period] = {
            currentPrice: `${basePrice}`,
            currentLink: tierLinks[tier as keyof typeof tierLinks] || `https://example.com/signup/tier${tier}`,
            defaultPrice: `${basePrice - 1}`,
            defaultLink: tierLinks[tier as keyof typeof tierLinks] || `https://example.com/signup/tier${tier}`,
            testPrices: [
              { price: `${basePrice + 2}`, link: tierLinks[tier as keyof typeof tierLinks] || `https://example.com/signup/tier${tier}` },
              { price: `${basePrice + 5}`, link: tierLinks[tier as keyof typeof tierLinks] || `https://example.com/signup/tier${tier}` }
            ]
          };
        } else {
          // Empty grid for manual entry
          newGrid[tier][period] = {
            currentPrice: '',
            currentLink: '',
            defaultPrice: '',
            defaultLink: '',
            testPrices: []
          };
        }
      }
    }
    setPricingGrid(newGrid);
  }, [tiers, billingPeriods, isTestMode]);

  const handleTierChange = (newTiers: number) => {
    setTiers(newTiers);
  };

  const handleBillingPeriodChange = (period: string, checked: boolean) => {
    if (checked) {
      setBillingPeriods([...billingPeriods, period]);
    } else {
      setBillingPeriods(billingPeriods.filter(p => p !== period));
    }
  };

  const handlePricingSave = (tier: number, period: string, data: PricingModalData) => {
    setPricingGrid(prev => ({
      ...prev,
      [tier]: {
        ...prev[tier],
        [period]: data
      }
    }));
  };

  const handlePricingContinue = (tier: number, period: string, data: PricingModalData) => {
    handlePricingSave(tier, period, data);
    
    // Find next tier/period to configure
    const currentTierIndex = tier - 1;
    const currentPeriodIndex = billingPeriods.indexOf(period);
    
    if (currentPeriodIndex < billingPeriods.length - 1) {
      // Next period in same tier
      setSelectedTier(tier);
      setSelectedPeriod(billingPeriods[currentPeriodIndex + 1]);
    } else if (currentTierIndex < tiers - 1) {
      // Next tier, first period
      setSelectedTier(tier + 1);
      setSelectedPeriod(billingPeriods[0]);
    } else {
      // All configured
      setSelectedTier(null);
      setSelectedPeriod(null);
    }
  };

  const handleComplete = () => {
    const pricingData: PricingData = {
      tiers,
      billingPeriods,
      pricingGrid
    };
    onComplete(pricingData);
  };

  const isConfigured = (tier: number, period: string) => {
    const data = pricingGrid[tier]?.[period];
    return data && data.currentPrice; // Only require currentPrice for configuration
  };

  const getConfiguredCount = () => {
    let count = 0;
    for (let tier = 1; tier <= tiers; tier++) {
      for (const period of billingPeriods) {
        if (isConfigured(tier, period)) count++;
      }
    }
    return count;
  };


  const totalConfigurations = tiers * billingPeriods.length;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Configure Your Pricing Table</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Test Mode Banner */}
          {isTestMode && (
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className="flex items-center">
                <div>
                  <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-1">
                    🧪 Test Mode Active
                  </h4>
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    Test pricing data has been automatically populated
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Predefined Data Selector */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Choose Predefined Pricing Data
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Select a predefined pricing configuration to auto-populate the grid below
            </p>
            <div className="flex items-center space-x-4">
              <Select onValueChange={handlePredefinedDataSelect}>
                <SelectTrigger className="w-80">
                  <SelectValue placeholder="Select predefined pricing data..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="table1_basic">
                    table1_basic - 3 tiers, 2 billing periods (monthly, annually)
                  </SelectItem>
                  <SelectItem value="table2_basic">
                    table2_basic - 4 tiers, 3 billing periods (monthly, quarterly, annual)
                  </SelectItem>
                  <SelectItem value="table3_enterprise">
                    table3_enterprise - 3 tiers, 3 billing periods (monthly, quarterly, annual)
                  </SelectItem>
                </SelectContent>
              </Select>
              <Button 
                variant="outline" 
                onClick={clearAllData}
                className="text-red-600 hover:text-red-700"
              >
                Clear All
              </Button>
            </div>
          </div>

          {/* Tier Configuration */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              How many tiers does this pricing table have?
            </h3>
            <div className="flex space-x-4">
              {[1, 2, 3, 4, 5].map((num) => (
                <Button
                  key={num}
                  variant={tiers === num ? "default" : "outline"}
                  onClick={() => handleTierChange(num)}
                >
                  {num}
                </Button>
              ))}
            </div>
          </div>

          {/* Billing Period Configuration */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Which billing periods does this pricing table have? Do you want them A/B tested?
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: 'monthly', label: 'Monthly' },
                { value: 'quarterly', label: 'Quarterly' },
                { value: 'semi_annually', label: 'Semi-Annually' },
                { value: 'annually', label: 'Annually' }
              ].map((option) => (
                <div key={option.value} className="flex items-center space-x-2">
                  <Checkbox
                    checked={billingPeriods.includes(option.value)}
                    onCheckedChange={(checked) => 
                      handleBillingPeriodChange(option.value, checked as boolean)
                    }
                  />
                  <Label className="cursor-pointer">{option.label}</Label>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pricing Grid */}
      {tiers > 0 && billingPeriods.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Please fill out your current and future prices for each tier and billing period</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-800">
                    <th className="border border-gray-300 dark:border-gray-600 p-3 text-left">
                      Billing Periods
                    </th>
                    {Array.from({ length: tiers }, (_, i) => i + 1).map((tier) => (
                      <th key={tier} className="border border-gray-300 dark:border-gray-600 p-3 text-left">
                        Tier {tier}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {billingPeriods.map((period) => (
                    <tr key={period}>
                      <td className="border border-gray-300 dark:border-gray-600 p-3 font-medium">
                        {period.charAt(0).toUpperCase() + period.slice(1).replace('_', ' ')}
                      </td>
                      {Array.from({ length: tiers }, (_, i) => i + 1).map((tier) => (
                        <td key={tier} className="border border-gray-300 dark:border-gray-600 p-3">
                          <Button
                            variant={isConfigured(tier, period) ? "default" : "outline"}
                            size="sm"
                            onClick={() => {
                              setSelectedTier(tier);
                              setSelectedPeriod(period);
                            }}
                            className="w-full"
                          >
                            {isConfigured(tier, period) ? 'Configured' : 'Configure'}
                          </Button>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button 
          onClick={handleComplete}
          disabled={getConfiguredCount() === 0}
        >
          Complete Configuration
        </Button>
      </div>

      {/* Pricing Modal */}
      <PricingModal
        isOpen={selectedTier !== null && selectedPeriod !== null}
        onClose={() => {
          setSelectedTier(null);
          setSelectedPeriod(null);
        }}
        onSave={handlePricingSave}
        onContinue={handlePricingContinue}
        tierNumber={selectedTier || 1}
        billingPeriod={selectedPeriod || ''}
        initialData={selectedTier && selectedPeriod ? pricingGrid[selectedTier]?.[selectedPeriod] : undefined}
      />
    </div>
  );
};

export default PricingInputGrid;
